import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, CircularProgress, InputLabel, TextField, Typography, useTheme, Select, MenuItem, Button, InputBase, IconButton } from "@mui/material";
import LeaveBalanceList from "./leaveBalanceList";
import styles from "./style";
import Modal from "../../../../components/Modal";
import ManageLeaveDaysModal from "../../../../modals/ManageLeaveDaysModal";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import moment from "moment-timezone";

dayjs.extend(customParseFormat);
import { Download } from "@mui/icons-material";
import { ReactComponent as Search } from "../../../../assets/images/searchIcon.svg";
import SortBy from "../../../../components/SortBy";
import AuthService from "../../../../api/AuthService";
import LoaderHelper from "../../../../utils/Loader/LoaderHelper";
import { useAlert } from "../../../../context/AlertContext";
import { refreshDateFormat } from "../../../../utils/dateFormatStore";

const LeaveManagementPage = ({ data, getLeaveType, handleDownload, handleLeaveApply, handleLeaveUpdate, reason, setReason, role, permission, handleFilter, handleSort, loading, employeeData, handleEmployee, open, setOpen, page, total, setPage, onCompanyChange, handleManageLeave, handleDeleteLeave, handleLeaveRequest, selectedLeaveType, handleLeaveTypeChange, setSelectedLeaveType }) => {
  const theme = useTheme();
  const isEmployeeRole = role === "Employee" || role === "employee";
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [leaveType, setLeaveType] = useState();
  const [optionValue, setOptionValue] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [selectedEmployeeEmail, setSelectedEmployeeEmail] = useState("");
  const employee = localStorage.getItem("employeeId");
  const [modalMode, setModalMode] = useState("create");
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [company, setCompany] = useState(localStorage.getItem("selectedCompany") || "");
  const [companyList, setCompanyList] = useState([]);
  const { showAlert } = useAlert();
  const [manageLeaveModalOpen, setManageLeaveModalOpen] = useState(false);
  const [selectedLeaveBalance, setSelectedLeaveBalance] = useState(null);
  const [manageActionType, setManageActionType] = useState("Add Leave"); 
  const [manageLeaveType, setManageLeaveType] = useState("");
  const [numberOfDays, setNumberOfDays] = useState("");
  const [manageNotes, setManageNotes] = useState("");

  const applyRequest = () => {
    const start = dayjs(fromDate);
    const end = dayjs(toDate);
    const dayCount = Math.max(1, end.diff(start, "day") + 1);
    const tz = moment.tz.guess();
    const isAdminOrSuperAdmin = role === "Admin" || role === "SuperAdmin";
    const requestId = isAdminOrSuperAdmin 
      ? (modalMode === "edit" ? selectedLeave?.employee?._id : employeeId)
      : employee;
      
    if (modalMode === "edit" && selectedLeave?._id) {
      handleLeaveUpdate(
        selectedLeave._id,
        leaveType,
        dayjs(fromDate).format("YYYY-MM-DD"),
        dayjs(toDate).format("YYYY-MM-DD"),
        dayCount,
        tz,
        reason
      );
    } else {
      handleLeaveApply(leaveType, fromDate, toDate, dayCount, tz, requestId, reason);
    }
  };

  const isFormValid = () => {
    if (!leaveType || !fromDate || !toDate || !reason) return false;
    if (modalMode === "create" && permission?.leave?.isEdit && role === "Admin" && !employeeId) return false;
    if (dayjs(toDate).isBefore(dayjs(fromDate), 'day')) return false;
    return true;
  };

  const hasEdits = useMemo(() => {
    if (modalMode !== "edit" || !selectedLeave) return false;
    
    const originalFromValue = selectedLeave.leaveFrom;
    const originalToValue = selectedLeave.leaveTo;
    const originalFrom = originalFromValue?.includes("/") 
      ? dayjs(originalFromValue, "DD/MM/YYYY") 
      : dayjs(originalFromValue);
    const originalTo = originalToValue?.includes("/") 
      ? dayjs(originalToValue, "DD/MM/YYYY") 
      : dayjs(originalToValue);
    
    const currentFrom = dayjs(fromDate);
    const currentTo = dayjs(toDate);

    return (
      (selectedLeave.leaveType || "") !== leaveType ||
      !originalFrom.isSame(currentFrom, "day") ||
      !originalTo.isSame(currentTo, "day") ||
      (selectedLeave.reason || "") !== reason
    );
  }, [modalMode, selectedLeave, leaveType, fromDate, toDate, reason]);

  const openCreateModal = () => {
    setModalMode("create");
    setSelectedLeave(null);
    setLeaveType("");
    setFromDate(dayjs());
    setToDate(dayjs());
    setReason("");
    setEmployeeId("");
    setOptionValue("");
    setSelectedEmployeeEmail("");
    setOpen(true);
  };

  const openManageModal = (leave) => {
    setSelectedLeaveBalance(leave);
    const leaveTypeName = leave?.leaveType || "";
    const leaveTypeObj = getLeaveType?.find((item) => item?.type === leaveTypeName);
    setManageLeaveType(leaveTypeObj?._id || "");
    setManageActionType("Add Leave");
    setNumberOfDays("");
    setManageNotes("");
    setManageLeaveModalOpen(true);
  };

  const openEditModal = (leave) => {
    setModalMode("edit");
    setSelectedLeave(leave);
    setLeaveType(leave?.leaveType || "");
    const fromDateValue = leave?.leaveFrom;
    const toDateValue = leave?.leaveTo;
    const parsedFromDate = fromDateValue?.includes("/") 
      ? dayjs(fromDateValue, "DD/MM/YYYY") 
      : dayjs(fromDateValue);
    const parsedToDate = toDateValue?.includes("/") 
      ? dayjs(toDateValue, "DD/MM/YYYY") 
      : dayjs(toDateValue);
    setFromDate(parsedFromDate.isValid() ? parsedFromDate : dayjs());
    setToDate(parsedToDate.isValid() ? parsedToDate : dayjs());
    setReason(leave?.reason || "");
    setSelectedEmployeeEmail(leave?.employee?.personalInfo?.email || "");
    setEmployeeId(leave?.employee?._id || "");
    setOpen(true);
  };

  useEffect(() => {
    if (!open) {
      setModalMode("create");
      setSelectedLeave(null);
      setLeaveType("");
      setFromDate(dayjs());
      setToDate(dayjs());
      setReason("");
      setEmployeeId("");
      setOptionValue("");
      setSelectedEmployeeEmail("");
    }
  }, [open]);

  useEffect(() => {
    if (!manageLeaveModalOpen) {
      setSelectedLeaveBalance(null);
      setManageActionType("Add Leave");
      setManageLeaveType("");
      setNumberOfDays("");
      setManageNotes("");
    }
  }, [manageLeaveModalOpen]);

  useEffect(() => {
    const permissionObj = localStorage.getItem("permission");
    const parsedPermissions = permissionObj ? JSON.parse(permissionObj) : null;
    const admin = localStorage.getItem("role");
    if (admin === "Admin" || parsedPermissions?.account?.isEdit) {
      getCompanyList();
    }
    const savedCompany = localStorage.getItem("selectedCompany");
    if (savedCompany) {
      setCompany(savedCompany);
    }
  }, []);

  const getCompanyList = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getCompantList();
      if (result?.success) {
        LoaderHelper.loaderStatus(false);
        setCompanyList(result?.data);
        const id = localStorage.getItem("companyId");
        const savedCompany = localStorage.getItem("selectedCompany");
        if (savedCompany) {
          setCompany(savedCompany);
        } else if (result?.data?.length > 0) {
          const value = result?.data?.filter(item => item?._id === id);
          setCompany(value[0]?.companyName || "");
        }
      } else {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "error");
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      showAlert(error, "error");
    }
  };

  const handleCompanyDetails = async (id, companyName) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.handleCompanyDetails(id);
      if (result?.success) {
        localStorage.setItem("accessToken", result?.data?.token);
        refreshDateFormat();
        localStorage.setItem("email", result?.data?.company?.email);
        localStorage.setItem("id", result?.data?.company?._id);
        localStorage.setItem("employeeId", result?.data?.role === "employee" ? result?.data?.userId : result?.data?.company?.admin);
        localStorage.setItem("selectedCompany", companyName);
        setCompany(companyName);
        LoaderHelper.loaderStatus(false);
        if (onCompanyChange) {
          onCompanyChange();
        }
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleLeaveTypeFilter = (value) => {
    if (handleLeaveTypeChange) {
      handleLeaveTypeChange(value);
    }
  };

  const handleSearchChange = (value) => {
    setSearchText(value);
    if (handleFilter) {
      handleFilter(value);
    }
  };

  const permissionObj = localStorage.getItem("permission");
  const parsedPermissions = permissionObj ? JSON.parse(permissionObj) : null;
  const admin = localStorage.getItem("role");
  const isAdminOrHasPermission = admin === "Admin" || parsedPermissions?.account?.isEdit;

  return (
    <>
      <Box px="15px" pt="20px" display="flex" justifyContent="space-between" alignItems="center">
        <Typography sx={styles.leaveHeading}>
          {isEmployeeRole ? "My Leave Management" : "Leave Management"}
        </Typography>
        <Box display="flex" gap={1}>
          {(permission?.attendance?.isView || role === "Admin") && (
            <Button
              variant="contained"
              onClick={openCreateModal}
              color="success"
              sx={{ borderRadius: 50, textTransform: "none", backgroundColor: "#28AA4A", fontSize: "12px", px: 3 }}
            >
              Add Leave
            </Button>
          )}
          <Button
            sx={styles.exportBtn}
            onClick={handleDownload}
          >
            <Download />
            Export Pdf
          </Button>
        </Box>
      </Box>
      <Box sx={{ backgroundColor: theme.palette.white.main, m: 2, borderRadius: "20px" }} >
        <Box display="flex" flexDirection={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} px="20px" pt="20px" pb="15px" gap={2} flexWrap="wrap">
          <Box
            display="flex"
            backgroundColor={theme.palette.lightGray.main}
            borderRadius="30px"
            width={{ xs: "100%", md: "auto" }}
            minWidth={{ md: "250px" }}
            justifyContent="center"
          >
            <IconButton type="button" sx={{ p: 1 }}>
              <Search />
            </IconButton>
            <InputBase 
              sx={{ flex: 1, fontSize: "12px" }} 
              placeholder="Search employee name" 
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)} 
            />
          </Box>

          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <Box sx={styles.inputBox} ml={1} mb={0.3}>
              <Select
                value={selectedLeaveType}
                onChange={(e) => handleLeaveTypeFilter(e.target.value)}
                name="leaveType"
                sx={styles.leaveTypeInput}
                displayEmpty
              >
                <MenuItem value="" sx={styles.dropDownMenu}>
                  All Leave Types
                </MenuItem>
                {getLeaveType?.map((item, index) => (
                  <MenuItem key={index} sx={styles.dropDownMenu} value={item?.type}>
                    {item?.type}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {(permission?.leave?.isView || role === "Admin" || role === "SuperAdmin") && (
              <SortBy handleSort={handleSort} />
            )}

            {isAdminOrHasPermission && (
              <Box sx={styles.inputBox} ml={1} mb={0.3}>
                <Select
                  value={company || ""}
                  onChange={(event) => { setCompany(event.target.value) }}
                  name="company"
                  sx={styles.hoursInput}
                  displayEmpty
                >
                  <MenuItem disabled value="" style={{ fontSize: 10 }}>
                    Select Company
                  </MenuItem>
                  {companyList?.map((item, index) => (
                    <MenuItem 
                      key={index} 
                      sx={styles.dropDownMenu} 
                      onClick={() => { handleCompanyDetails(item?._id, item.companyName) }}
                      value={item.companyName}
                    >
                      {item.companyName}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}
          </Box>
        </Box>

        <LeaveBalanceList
          data={data}
          setPage={setPage}
          page={page}
          total={total}
          selectedLeaveType={selectedLeaveType}
          canEdit={permission?.leave?.isEdit || role === "Admin" || role === "SuperAdmin"}
          handleEdit={openEditModal}
          handleManage={openManageModal}
          handleDelete={handleDeleteLeave}
        />

        <ManageLeaveDaysModal
          open={manageLeaveModalOpen}
          setOpen={setManageLeaveModalOpen}
          loading={loading}
          getLeaveType={getLeaveType}
          manageActionType={manageActionType}
          setManageActionType={setManageActionType}
          manageLeaveType={manageLeaveType}
          setManageLeaveType={setManageLeaveType}
          numberOfDays={numberOfDays}
          setNumberOfDays={setNumberOfDays}
          manageNotes={manageNotes}
          setManageNotes={setManageNotes}
          employeeId={selectedLeaveBalance?.employee?._id || selectedLeaveBalance?.employee}
          handleSave={async (action, employeeId, leaveType, numberOfDays, notes) => {
            const success = await handleManageLeave(action, employeeId, leaveType, numberOfDays, notes);
            if (success) {
              setManageLeaveModalOpen(false);
            }
            return success;
          }}
        />

        <Modal
          open={open}
          disabled={modalMode === "edit" ? (selectedLeave?.status === "Approved" || !isFormValid() || !hasEdits) : !isFormValid()}
          setOpen={setOpen}
          btnText={modalMode === "edit" ? "Update" : "Apply"}
          title={modalMode === "edit" ? "Update Leave Request" : "Leave Request"}
          handleCheck={applyRequest}
          loading={loading}
        >
          {modalMode === "edit" && (role === "Admin" || role === "SuperAdmin") && selectedLeave && (
            <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 2, mb: 3 }}>
              {selectedLeave?.status === "Pending" && (
                <>
                  <Button
                    type="button"
                    onClick={() => handleLeaveRequest(selectedLeave?._id || selectedLeave?.id, "Rejected")}
                    disabled={loading}
                    sx={{
                      color: "red",
                      backgroundColor: "#E9303033",
                      borderRadius: "20px",
                      px: 4,
                      py: 1,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#E9303050",
                      },
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    type="button"
                    onClick={() => handleLeaveRequest(selectedLeave?._id || selectedLeave?.id, "Approved")}
                    disabled={loading}
                    sx={{
                      color: "#28AA4A",
                      backgroundColor: "#28AA4A33",
                      borderRadius: "20px",
                      px: 4,
                      py: 1,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#28AA4A50",
                      },
                    }}
                  >
                    Approve
                  </Button>
                </>
              )}
              {selectedLeave?.status === "Rejected" && (
                <Button
                  type="button"
                  onClick={() => handleLeaveRequest(selectedLeave?._id || selectedLeave?.id, "Approved")}
                  disabled={loading}
                  sx={{
                    color: "#28AA4A",
                    backgroundColor: "#28AA4A33",
                    borderRadius: "20px",
                    px: 4,
                    py: 1,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#28AA4A50",
                    },
                  }}
                >
                  Approve
                </Button>
              )}
            </Box>
          )}
          <Box>
            {modalMode === "create" && (permission?.leave?.isEdit || role === "Admin" || role === "SuperAdmin") ?
              <Autocomplete
                id="autocomplete"
                options={employeeData || []}
                getOptionLabel={(option) => option?.personalInfo?.email}
                onInputChange={(event, newInputValue) => {
                  handleEmployee(newInputValue);
                }}
                onChange={(event, newValue) => {
                  setOptionValue(newValue);
                  setEmployeeId(newValue?._id)
                }}

                renderInput={(params) => (
                  <><InputLabel sx={styles.label}>{"Employee Email"}</InputLabel><TextField
                    {...params}
                    sx={{ ...styles.modalInput, ...styles.inputSpace }}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }} /></>
                )}
              /> : null}
            {modalMode === "edit" && (
              <Box sx={{ mt: 2 }}>
                <InputLabel sx={styles.label}>Employee Email</InputLabel>
                <TextField
                  value={selectedEmployeeEmail}
                  sx={{
                    ...styles.modalInput,
                    "& .MuiInputBase-input": {
                      color: "#666",
                      cursor: "not-allowed",
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                  disabled
                  InputProps={{ readOnly: true }}
                />
              </Box>
            )}
          </Box>
          <Box>
            <InputLabel sx={styles.label}>Leave Type</InputLabel>
            <Select
              value={leaveType}
              onChange={(e) => setLeaveType(e.target.value)}
              sx={styles.dropdown}
            >
              <MenuItem disabled value="">
                Select Type
              </MenuItem>
              {getLeaveType?.map((items, index) => (

                <MenuItem sx={styles.dropDownMenu} key={index} value={items?.type}>
                  {items?.type}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker", "DatePicker"]} sx={styles.calender}>
              <Box>
                <InputLabel sx={styles.label}>From</InputLabel>
                <DatePicker 
                  value={fromDate} 
                  {...(role !== "Admin" && role !== "SuperAdmin" && { minDate: dayjs() })}
                  onChange={(newValue) => { setFromDate(newValue) }} 
                  sx={styles.calenderInput} 
                  slotProps={{
                    field: {
                      readOnly: true
                    }
                  }}
                />
              </Box>
              <Box>
                <InputLabel sx={styles.label}>To</InputLabel>
                <DatePicker
                  value={toDate}
                  slotProps={{
                    field: {
                      readOnly: true
                    }
                  }}
                  {...(role !== "Admin" && role !== "SuperAdmin" && { minDate: fromDate })}
                  sx={styles.calenderInput}
                  onChange={(newValue) => { setToDate(newValue); }}
                />
              </Box>
            </DemoContainer>
          </LocalizationProvider>
          <Box sx={{ my: 1 }}>
            <InputLabel sx={styles.label}>Reason</InputLabel>
            <TextField
              multiline={true}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              sx={styles.reason}
            />
          </Box>
        </Modal>

      </Box>
    </>
  );
};

export default LeaveManagementPage;
